import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarHeart, Megaphone, ArrowRight, Pin } from "lucide-react";
import { toast } from "sonner";
import { HOLIDAY_TYPES, dayLabel, todayIso } from "@/lib/hr";
import { timeAgo } from "@/lib/ui";

const PROFILE_ROWS = [["Employee ID", "employee_id"], ["Designation", "designation"], ["Department", "department"], ["Date of joining", "joining_date"], ["Location", "location"], ["Phone", "phone"], ["Bank", "bank_name"], ["Account no.", "bank_account"], ["IFSC", "ifsc"], ["PAN", "pan"], ["UAN", "uan"]];

export function MyProfileTab() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/hr/profiles/me").then((r) => setP(r.data)); }, []);
  if (!p) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-2xl" data-testid="my-profile-tab">
      <p className="font-display font-semibold text-slate-900">{p.user.name}</p>
      <p className="text-xs text-slate-500 mb-4">{p.user.email} · details are maintained by HR</p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {PROFILE_ROWS.map(([l, k]) => <div key={k}><dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{l}</dt><dd className="text-sm text-slate-800" data-testid={`my-profile-${k}`}>{p[k] || "—"}</dd></div>)}
      </dl>
    </div>
  );
}

export function HrSettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/hr/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try { await api.put("/hr/settings", s); toast.success("HR settings saved"); } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-xl space-y-3" data-testid="hr-settings-tab">
      <p className="text-sm text-slate-500">Printed on every payslip header.</p>
      <div className="space-y-1.5"><Label>Company legal name</Label><Input value={s.company_name} onChange={(e) => setS({ ...s, company_name: e.target.value })} placeholder="[Company legal name]" data-testid="hr-company-name-input" /></div>
      <div className="space-y-1.5"><Label>Registered address</Label><Textarea value={s.company_address} onChange={(e) => setS({ ...s, company_address: e.target.value })} placeholder="[Company address]" data-testid="hr-company-address-input" /></div>
      <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="hr-settings-save-button">Save</Button>
    </div>
  );
}

function Card({ icon: Icon, title, tint, link, testid, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><span className={`w-7 h-7 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-4 h-4" /></span><h3 className="font-display font-semibold text-slate-900">{title}</h3></div>
        <Link to={link} className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1" data-testid={`${testid}-link`}>View <ArrowRight className="w-3 h-3" /></Link>
      </div>
      {children}
    </div>
  );
}

export function UpcomingHolidaysCard() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/hr/holidays").then((r) => setList(r.data.filter((h) => h.date >= todayIso()).slice(0, 4))); }, []);
  return (
    <Card icon={CalendarHeart} title="Upcoming holidays" tint="bg-rose-50 text-rose-600" link="/hr?tab=holidays" testid="upcoming-holidays-card">
      <div className="space-y-2.5">
        {list.map((h) => (
          <div key={h.id} className="flex items-center gap-3 text-sm"><span className={`w-2 h-2 rounded-full shrink-0 ${HOLIDAY_TYPES[h.type]?.dot}`} /><span className="flex-1 truncate text-slate-800">{h.name}</span><span className="text-xs text-slate-500 shrink-0">{dayLabel(h.date, { weekday: "short", day: "numeric", month: "short" })}</span></div>
        ))}
        {!list.length && <p className="text-sm text-slate-400">No upcoming holidays added.</p>}
      </div>
    </Card>
  );
}

export function AnnouncementsCard() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/hr/announcements").then((r) => setList(r.data.slice(0, 2))); }, []);
  if (!list.length) return null;
  return (
    <Card icon={Megaphone} title="Announcements" tint="bg-indigo-50 text-indigo-600" link="/hr?tab=announcements" testid="announcements-card">
      <div className="space-y-3">
        {list.map((a) => (
          <div key={a.id}><p className="text-sm font-medium text-slate-900 flex items-center gap-1">{a.pinned && <Pin className="w-3 h-3 text-indigo-500" />}{a.title}</p><p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{a.body}</p><p className="text-[11px] text-slate-400 mt-0.5">{timeAgo(a.created_at)}</p></div>
        ))}
      </div>
    </Card>
  );
}
